import React, { useState } from 'react';
import {
  IAuditoriaCorrelacaoFiscalResultado,
  IAuditoriaCorrelacaoFiscalItem,
  IAuditoriaCorrelacaoFiscalMes,
  IAuditoriaCorrelacaoFiscalLancamento,
} from '../interfaces/IAuditoriaCorrelacaoFiscal';

interface Props {
  resultado: IAuditoriaCorrelacaoFiscalResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (value?: number) =>
  `${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// COR-261: teto de severidade agora é ALERTA — a auditoria nunca mais classifica ERRO.
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ALERTA'        ? 'badge-light-warning' :
    status === 'AVISO'         ? 'badge-light-warning' :
    status === 'SEM_HISTORICO' ? 'badge-light-secondary' :
                                 'badge-light-success';
  return <span className={`badge ${cls}`}>{status === 'SEM_HISTORICO' ? 'Sem Histórico' : status}</span>;
};

// COR-200: mini-tabela de lançamentos individuais — memória de cálculo por natureza
// (receita ou tributo), com conta, descrição, data, histórico e valor.
const TabelaLancamentos: React.FC<{ titulo: string; lancamentos: IAuditoriaCorrelacaoFiscalLancamento[] }> = ({ titulo, lancamentos }) => (
  <div className='mb-3'>
    <div className='fw-bold text-muted fs-9 mb-1'>{titulo} ({lancamentos.length} lançamento(s))</div>
    {lancamentos.length === 0 ? (
      <div className='text-muted fs-9 fst-italic'>Nenhum lançamento na competência.</div>
    ) : (
      <div className='table-responsive' style={{ maxHeight: 180, overflowY: 'auto' }}>
        <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-1 fs-9'>
          <thead>
            <tr className='fw-bolder text-muted'>
              <th>Conta</th>
              <th>Data</th>
              <th>Histórico</th>
              <th className='text-end'>Valor</th>
            </tr>
          </thead>
          <tbody>
            {lancamentos.map((l, i) => (
              <tr key={i}>
                <td>{l.conta}{l.descricaoConta ? ` — ${l.descricaoConta}` : ''}</td>
                <td>{l.data}</td>
                <td>{l.historico}</td>
                <td className='text-end'>{formatCurrency(l.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// COR-199: tabela mensal completa — toda competência avaliada (receita, PIS, COFINS, ICMS,
// ISS, alíquota efetiva, média móvel, desvio), não só as que viraram ocorrência — pedida para
// conferência/homologação técnica da regra.
// COR-200: cada linha é expansível e mostra a memória de cálculo (lançamentos por natureza,
// contas utilizadas e critério de classificação aplicado).
const MesRow: React.FC<{ m: IAuditoriaCorrelacaoFiscalMes }> = ({ m }) => {
  const [expanded, setExpanded] = useState(false);
  const semHistorico = m.status === 'SEM_HISTORICO';

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{m.mesAno}</span>
        </td>
        <td className='text-end'>
          {formatCurrency(m.receitaOperacional)}
          {m.receitaNaoOperacional > 0 && (
            <div className='text-muted fs-9'>+ {formatCurrency(m.receitaNaoOperacional)} não operacional</div>
          )}
        </td>
        <td className='text-end'>{formatCurrency(m.pisProvisionado)}</td>
        <td className='text-end'>{formatCurrency(m.cofinsProvisionado)}</td>
        <td className='text-end'>{formatCurrency(m.icmsProvisionado)}</td>
        <td className='text-end'>{formatCurrency(m.issProvisionado)}</td>
        <td className='text-end fw-bold'>{formatCurrency(m.impostosTotal)}</td>
        <td className='text-center'>{formatPct(m.aliquotaEfetiva)}</td>
        <td className='text-center text-muted'>{semHistorico ? '—' : formatPct(m.aliquotaMediaMovel)}</td>
        <td className='text-center'>{semHistorico ? '—' : formatPct(m.percentualDesvio)}</td>
        <td className='text-center'><StatusBadge status={m.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={11} className='bg-light'>
            <div className='p-3'>
              {m.criterioClassificacao && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mb-2'>
                  <strong>Critério de Classificação:</strong> {m.criterioClassificacao}
                </div>
              )}
              {m.causaProvavelQueda && (
                <div className='alert alert-light border border-info p-2 fs-9 mb-2'>
                  <strong>Causa Provável da Queda:</strong> {m.causaProvavelQueda}
                </div>
              )}
              <div className='fw-bold fs-8 text-muted mb-2'>Memória de Cálculo — Lançamentos da ECD ({m.mesAno})</div>
              <TabelaLancamentos titulo='Receita Operacional (base da Alíquota Efetiva)' lancamentos={m.lancamentosReceita} />
              <TabelaLancamentos titulo='Receita Não Operacional (não entra no cálculo)' lancamentos={m.lancamentosReceitaNaoOperacional} />
              <TabelaLancamentos titulo='PIS' lancamentos={m.lancamentosPis} />
              <TabelaLancamentos titulo='COFINS' lancamentos={m.lancamentosCofins} />
              <TabelaLancamentos titulo='ICMS' lancamentos={m.lancamentosIcms} />
              <TabelaLancamentos titulo='ISS' lancamentos={m.lancamentosIss} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const TabelaSerieMensal: React.FC<{ serie: IAuditoriaCorrelacaoFiscalMes[] }> = ({ serie }) => {
  const ordenada = [...serie].sort((a, b) => a.mesAno.localeCompare(b.mesAno));
  return (
    <div className='table-responsive'>
      <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 fs-8'>
        <thead>
          <tr className='fw-bolder text-muted'>
            <th>Mês/Ano</th>
            <th className='text-end'>Receita</th>
            <th className='text-end'>PIS</th>
            <th className='text-end'>COFINS</th>
            <th className='text-end'>ICMS</th>
            <th className='text-end'>ISS</th>
            <th className='text-end'>Impostos</th>
            <th className='text-center'>AE Mensal</th>
            <th className='text-center'>AE Média 6m</th>
            <th className='text-center'>Desvio</th>
            <th className='text-center'>Status</th>
          </tr>
        </thead>
        <tbody>
          {ordenada.map((m, i) => (
            <MesRow key={i} m={m} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ItemRow: React.FC<{ item: IAuditoriaCorrelacaoFiscalItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td className='text-center fw-bold'>{item.mesAno}</td>
        <td className='text-end fw-bold'>
          {formatCurrency(item.receitaOperacional)}
          {item.receitaNaoOperacional > 0 && (
            <div className='text-muted fs-9 fw-normal'>+ {formatCurrency(item.receitaNaoOperacional)} não operacional</div>
          )}
        </td>
        <td className='text-end'>{formatCurrency(item.impostosTotal)}</td>
        <td className='text-center'>{formatPct(item.aliquotaEfetiva)}</td>
        <td className='text-center text-muted'>{formatPct(item.aliquotaMediaMovel)}</td>
        <td className='text-center fw-bolder text-danger'>{formatPct(item.percentualDesvio)}</td>
        <td className='text-end fw-bold text-muted'>{formatCurrency(item.passivoReferencia)}</td>
        <td className='text-center'><StatusBadge status={item.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={9} className='bg-light'>
            <div className='p-4'>
              {/* Breakdown de impostos */}
              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>PIS Provisionado</div>
                    <div className='fw-bolder fs-6'>{formatCurrency(item.pisProvisionado)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>COFINS Provisionado</div>
                    <div className='fw-bolder fs-6'>{formatCurrency(item.cofinsProvisionado)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>ICMS Provisionado</div>
                    <div className='fw-bolder fs-6'>{formatCurrency(item.icmsProvisionado)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>ISS Provisionado</div>
                    <div className='fw-bolder fs-6'>{formatCurrency(item.issProvisionado)}</div>
                  </div>
                </div>
              </div>

              {/* COR-261: passivo "confirmado" — nunca calculado automaticamente, sempre R$ 0,00
                  (confirmar infração fiscal depende de validação documental). */}
              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Impacto Fiscal Confirmado</div>
                    <div className='fw-bolder fs-5 text-success'>{formatCurrency(item.passivoTributario)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Diferença Estimada (Referência)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.diferencaReferencia)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Multa + SELIC (Referência)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.multaReferencia + item.jurosReferencia)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Referência Informativa Total</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.passivoReferencia)}</div>
                  </div>
                </div>
              </div>

              {item.criterioClassificacao && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mb-2'>
                  <strong>Critério de Classificação:</strong> {item.criterioClassificacao}
                </div>
              )}

              {item.causaProvavelQueda && (
                <div className='alert alert-light border border-info p-2 fs-9 mb-2'>
                  <strong>Causa Provável da Queda:</strong> {item.causaProvavelQueda}
                </div>
              )}

              {item.passivoReferencia > 0 && (
                <div className='alert alert-light-primary border border-primary p-2 fs-9 mb-2'>
                  <strong>Referência informativa (não confirmada):</strong> calculada só sobre o(s)
                  tributo(s) com indício individual de divergência — pendente de validação documental
                  (nota fiscal, município, retenção, código de serviço e alíquota aplicável).
                </div>
              )}

              <div className='alert alert-light-warning border border-warning p-3 fs-8'>
                {item.mensagemDiagnostico}
              </div>

              {/* COR-301: memória de cálculo — os lançamentos individuais que compuseram a
                  ocorrência, sem precisar abrir a tabela mensal completa separadamente. */}
              <div className='fw-bold fs-8 text-muted mt-3 mb-2'>Memória de Cálculo — Lançamentos da ECD ({item.mesAno})</div>
              <TabelaLancamentos titulo='Receita Operacional (base da Alíquota Efetiva)' lancamentos={item.lancamentosReceita} />
              <TabelaLancamentos titulo='Receita Não Operacional (não entra no cálculo)' lancamentos={item.lancamentosReceitaNaoOperacional} />
              <TabelaLancamentos titulo='PIS' lancamentos={item.lancamentosPis} />
              <TabelaLancamentos titulo='COFINS' lancamentos={item.lancamentosCofins} />
              <TabelaLancamentos titulo='ICMS' lancamentos={item.lancamentosIcms} />
              <TabelaLancamentos titulo='ISS' lancamentos={item.lancamentosIss} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaCorrelacaoFiscalDetalhe: React.FC<Props> = ({ resultado }) => {
  const [mostrarSerie, setMostrarSerie] = useState(false);

  if (!resultado) return null;

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Detalhamento — Correlação Receita × Impostos (PIS/COFINS/ICMS/ISS)
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.itens.length} mês(es) com possível variação na alíquota efetiva · Impacto fiscal confirmado:{' '}
            <strong className='text-success'>{(resultado.passivoTributarioTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
          </span>
        </h3>
      </div>

      {/* COR-261: passivo "confirmado" nunca automático — StatusDocumentacao equivalente não
          existe neste módulo, mas o mesmo princípio se aplica: sem validação documental, o
          impacto fiscal confirmado é sempre R$ 0,00. A referência informativa (COR-262) é
          exibida separadamente, nunca somada ao valor acima. */}
      {resultado.passivoReferenciaTotal > 0 && (
        <div className='card-body pb-0'>
          <div className='alert alert-light-primary border border-primary p-3 fs-8 mb-0'>
            <strong>Referência informativa (não confirmada):</strong> diferença estimada só para
            tributos com indício individual de divergência (nunca a partir do desvio agregado da
            alíquota efetiva total):{' '}
            <strong>{resultado.passivoReferenciaTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>.
            Pendente de validação documental do tributo, competência, município, serviço e natureza da receita.
          </div>
        </div>
      )}

      {resultado.mensagemDirecionamento && (
        <div className='card-body pb-0'>
          <div className='alert alert-light-warning border border-warning p-3 fs-8'>
            {resultado.mensagemDirecionamento}
          </div>
        </div>
      )}

      <div className='card-body pt-3'>
        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhum desvio de alíquota efetiva identificado. Correlação Receita × Impostos está regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th className='text-center'>Mês/Ano</th>
                  <th className='text-end'>Receita</th>
                  <th className='text-end'>Impostos</th>
                  <th className='text-center'>AE Mensal</th>
                  <th className='text-center'>AE Média 6m</th>
                  <th className='text-center'>Desvio</th>
                  <th className='text-end'>Referência Informativa</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemRow key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* COR-199: tabela mensal completa — todas as competências avaliadas, para
            conferência/homologação técnica da regra, não só as que viraram ocorrência. */}
        {resultado.serieMensal.length > 0 && (
          <div className='mt-5'>
            <button
              className='btn btn-sm btn-light-primary mb-3'
              onClick={() => setMostrarSerie(!mostrarSerie)}
            >
              <i className={`bi bi-chevron-${mostrarSerie ? 'up' : 'down'} me-1`} />
              {mostrarSerie ? 'Ocultar' : 'Exibir'} tabela mensal completa ({resultado.serieMensal.length} competência(s))
            </button>
            {mostrarSerie && <TabelaSerieMensal serie={resultado.serieMensal} />}
          </div>
        )}

        {resultado.fundamentacaoLegal && (
          <div className='alert alert-light border border-secondary p-3 fs-9 mt-4'>
            <strong>Fundamentação Legal:</strong> {resultado.fundamentacaoLegal}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaCorrelacaoFiscalDetalhe;
